import { ArrowLeft, Edit2, Trash2, CheckCircle2, Circle, MoreVertical } from 'lucide-react';
import { useState } from 'react';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isOverdue(dueDate) {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + 'T00:00:00');
  return due < today;
}

export default function GoalHeader({
  goal,
  progressPercent,
  onClose,
  onEdit,
  onDelete,
  onToggleComplete,
  isAdmin
}) {
  const [showMenu, setShowMenu] = useState(false);
  const overdue = isOverdue(goal.dueDate) && !goal.completed;

  // Progress ring parameters
  const size = 56;
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progressPercent / 100) * circumference;

  return (
    <div className="mb-6">
      {/* Top row: Back button and actions */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onClose}
          className="hidden md:flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="text-sm">Back to Goals</span>
        </button>

        {isAdmin && (
          <div className="relative ml-auto">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 rounded-lg hover:bg-surface-hover transition-colors"
            >
              <MoreVertical size={18} className="text-text-secondary" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 top-full mt-1 bg-surface-card border border-border rounded-lg shadow-lg z-20 py-1 min-w-[140px]">
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      onEdit();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-surface-hover flex items-center gap-2"
                  >
                    <Edit2 size={14} />
                    Edit Goal
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false);
                      onDelete();
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-red-500 hover:bg-red-500/10 flex items-center gap-2"
                  >
                    <Trash2 size={14} />
                    Delete Goal
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Main header content */}
      <div className="flex items-start gap-4">
        {/* Progress Ring */}
        <div className="relative flex-shrink-0">
          <svg width={size} height={size} className="transform -rotate-90">
            {/* Background circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth={strokeWidth}
              className="text-border"
            />
            {/* Progress circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className={goal.completed ? 'text-green-500' : 'text-accent'}
              style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
            />
          </svg>
          {/* Percentage text */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-sm font-semibold ${goal.completed ? 'text-green-500' : 'text-text-primary'}`}>
              {progressPercent}%
            </span>
          </div>
        </div>

        {/* Title and meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3">
            {/* Completion checkbox */}
            {isAdmin && (
              <button
                onClick={onToggleComplete}
                className="mt-1 flex-shrink-0"
              >
                {goal.completed ? (
                  <CheckCircle2 size={22} className="text-green-500" />
                ) : (
                  <Circle size={22} className="text-text-muted hover:text-accent transition-colors" />
                )}
              </button>
            )}

            <div className="flex-1 min-w-0">
              <h1 className={`text-xl font-semibold text-text-primary ${goal.completed ? 'line-through opacity-60' : ''}`}>
                {goal.title}
              </h1>

              {goal.description && (
                <p className={`text-sm text-text-secondary mt-1 ${goal.completed ? 'opacity-60' : ''}`}>
                  {goal.description}
                </p>
              )}

              {/* Meta info */}
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className="text-xs text-text-tertiary bg-surface-hover px-2 py-0.5 rounded">
                  {goal.owner || 'Unassigned'}
                </span>
                <span className="text-text-muted">·</span>
                <span className={`text-xs ${overdue ? 'text-red-500 font-medium' : 'text-text-tertiary'}`}>
                  {overdue && 'OVERDUE - '}
                  Due {formatDate(goal.dueDate)}
                </span>
                {goal.completed && (
                  <>
                    <span className="text-text-muted">·</span>
                    <span className="text-xs text-green-500 font-medium">Completed</span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
